import React, { useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { Circle, GoogleMap, Marker, OverlayViewF, useJsApiLoader } from "@react-google-maps/api";
import { designColors, designFontFamily, designType } from "services/constants/designTokens";

// "Location on Map": the site's pin, its geo-fence circle, the coordinates
// and an "Open in Google Maps" link. Shared by Team Workspace > Settings >
// Geo Fenced Sites and Administration > Sites.

// Loader options must match the other Google Maps screens (Operations > Work
// Sessions); the script can only be loaded once per page with one set of
// options.
const MAP_LIBRARIES = ["maps", "geometry"];
// The map is zoomed to the geo-fence, so the circle fills the map whatever
// its radius and the name always has room inside it. Google's zoom levels
// are whole numbers, so the circle's radius on screen ends up between half
// of MAP_FENCE_RADIUS_PX and MAP_FENCE_RADIUS_PX.
const MAP_HEIGHT = 200;
const MAP_FENCE_RADIUS_PX = 96;
const MAP_DEFAULT_ZOOM = 17;
const MAP_MIN_ZOOM = 3;
const MAP_MAX_ZOOM = 20;
// Ground metres covered by one pixel at zoom 0 on the equator (Web Mercator).
const METRES_PER_PIXEL_AT_ZOOM_0 = 156543.03392;
const metresPerPixel = (lat, zoom) => (METRES_PER_PIXEL_AT_ZOOM_0 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
export const zoomForRadius = (lat, radius) => {
  if (!(radius > 0)) return MAP_DEFAULT_ZOOM;
  const zoom = Math.floor(Math.log2((metresPerPixel(lat, 0) * MAP_FENCE_RADIUS_PX) / radius));
  return Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, zoom));
};
// The name sits just under the pin's tip, inside the circle. A fence too
// small for that even at full zoom gets the name below the circle instead,
// so the two never overlap.
const MAP_LABEL_GAP = 6;
const MAP_LABEL_HEIGHT = 20;
export const mapLabelDrop = (lat, radius, zoom) => {
  if (!(radius > 0)) return MAP_LABEL_GAP;
  const radiusPx = radius / metresPerPixel(lat, zoom);
  return radiusPx >= MAP_LABEL_GAP + MAP_LABEL_HEIGHT + 8 ? MAP_LABEL_GAP : Math.ceil(radiusPx) + 4;
};

const openInMaps = (latitude, longitude) =>
  window.open(`https://www.google.com/maps?q=${latitude},${longitude}&z=15`, "_blank");

const hasValue = (value) => value != null && String(value).trim() !== "";

export const SiteLocationMap = ({
  latitude,
  longitude,
  radius: radiusValue,
  label,
  emptyText = "No coordinates are saved for this site.",
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDDC3xzU7WkPldGiqEkB6LkZyXrGsI_coA",
    id: "google-map-script",
    libraries: MAP_LIBRARIES,
  });
  const lat = Number(latitude);
  const lng = Number(longitude);
  const radius = Number(radiusValue) || 0;
  const hasPosition = hasValue(latitude) && hasValue(longitude) && Number.isFinite(lat) && Number.isFinite(lng);
  const center = useMemo(() => ({ lat, lng }), [lat, lng]);
  const mapLabel = String(label || "").trim();
  const zoom = zoomForRadius(lat, radius);
  const labelDrop = mapLabelDrop(lat, radius, zoom);
  // Centres the name horizontally on the pin.
  const mapLabelOffset = useCallback((width) => ({ x: -(width / 2), y: labelDrop }), [labelDrop]);

  if (!hasPosition) {
    return <Mui.Typography sx={{ ...designType.helperText, py: 2 }}>{emptyText}</Mui.Typography>;
  }

  return (
    <Mui.Box sx={{ py: 1.5 }}>
      <Mui.Box sx={{ height: MAP_HEIGHT, borderRadius: 2, overflow: "hidden", backgroundColor: designColors.slate100 }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={zoom}
            options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
          >
            <Marker position={center} />
            {mapLabel && (
              <OverlayViewF position={center} mapPaneName="overlayLayer" getPixelPositionOffset={mapLabelOffset}>
                <Mui.Box
                  sx={{
                    px: 0.5,
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: designFontFamily,
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: designColors.slate900,
                    // White halo keeps the name readable over roads and the
                    // tinted geo-fence circle.
                    textShadow: "0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff",
                    pointerEvents: "none",
                  }}
                >
                  {mapLabel}
                </Mui.Box>
              </OverlayViewF>
            )}
            {radius > 0 && (
              <Circle
                center={center}
                radius={radius}
                options={{
                  strokeColor: designColors.tealDark,
                  strokeOpacity: 0.9,
                  strokeWeight: 1.5,
                  fillColor: designColors.teal,
                  fillOpacity: 0.18,
                  clickable: false,
                }}
              />
            )}
          </GoogleMap>
        ) : null}
      </Mui.Box>
      <Mui.Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" sx={{ mt: 1.25 }}>
        <Mui.Typography sx={designType.helperText}>
          Latitude{" "}
          <Mui.Box component="span" sx={{ color: designColors.slate900, fontWeight: 600 }}>
            {latitude}
          </Mui.Box>
        </Mui.Typography>
        <Mui.Typography sx={designType.helperText}>
          Longitude{" "}
          <Mui.Box component="span" sx={{ color: designColors.slate900, fontWeight: 600 }}>
            {longitude}
          </Mui.Box>
        </Mui.Typography>
        <Mui.Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => openInMaps(latitude, longitude)}
          sx={{ fontSize: 13, fontWeight: 600, color: designColors.tealDark }}
        >
          Open in Google Maps
        </Mui.Link>
      </Mui.Stack>
    </Mui.Box>
  );
};
